import { useMutation, useQuery } from "@apollo/client";
import { useContext, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { usePaystackPayment } from "react-paystack";
import { PaystackProps } from "react-paystack/dist/types";
import { useNavigate } from "react-router-dom";
import { InawoGreenLuxeIcon } from "src/components/Icons";
import { AppContext, ValueProps } from "src/context";
import {
  GenerateLuxePaymentMetadata,
  UpgradeToLuxe,
  VerifyTransaction,
} from "src/graphql/mutations";
import { GetEvent, GetUsername } from "src/graphql/queries";
import { GetEventWallets } from "src/graphql/queries/wallets.queries";
import useAppStore from "src/store/utils";
import {
  getUsername,
  upgradeToLuxevariables,
  verifyTransaction,
  verifyTransactionVariables,
} from "src/types/api.d";
import {
  GenerateLuxePaymentMetadataVariables,
  GetWallets,
  GetWalletsVariables,
  generateLuxePaymentMetadata,
} from "src/utils/gql_types";
import SendInvitesModalFooter from "./PayForLuxeModalFooter";
import PaymentOption from "./PaymentOption";

interface PaymentOptionsProps {
  setOpen: (value: boolean) => void;
  cost: number | undefined;
}

const PaymentOptions = ({ setOpen, cost }: PaymentOptionsProps) => {
  const [active, setActive] = useState<string>("");
  const navigate = useNavigate();
  const [userDetails, setUserDetails] = useState<getUsername>();
  const contextValue = useContext<ValueProps | null>(AppContext);
  const isAdmin = contextValue?.isAdmin;
  const { event } = useAppStore((state) => state);

  useQuery<getUsername>(GetUsername, {
    fetchPolicy: "network-only",
    onCompleted(response) {
      setUserDetails(response);
    },
    onError(error) {
      if (error?.message !== "unauthenticated") {
        toast.error(
          <p className="toast">{error?.message ?? "An error occured"}</p>
        );
      } else {
        navigate("/clear");
      }
    },
  });

  const updateActiveElement = (id: string) => {
    setActive(active !== id ? id : "");
  };

  const [payForLuxe, { loading }] = useMutation<upgradeToLuxevariables>(
    UpgradeToLuxe,
    {
      onCompleted() {
        toast.success(
          <p className="toast">{"Inawo Luxe payment successful"}</p>
        );
        setOpen(false);
        navigate(0);
      },
      onError(error) {
        if (error?.message !== "unauthenticated") {
          toast.error(
            <p className="toast">{error?.message ?? "An error occured"}</p>
          );
        } else {
          navigate("/clear");
        }
      },
    }
  );

  const upgradeToLuxe = () => {
    payForLuxe({
      variables: {
        eventId: event.uuid,
      },
    });
  };

  const [verifyTransaction] = useMutation<
    verifyTransaction,
    verifyTransactionVariables
  >(VerifyTransaction, {
    onCompleted: (res) => {
      if (res.verifyTransaction.reference) {
        setTimeout(() => {
          upgradeToLuxe();
        }, 100);
      }
    },
    refetchQueries: [
      {
        query: GetEvent,
        variables: {
          eventId: event.id,
        },
      },
    ],
    onError(error) {
      if (error.message !== "unauthenticated") {
        toast.error(
          <p className="toast">{error?.message ?? "An error occured"}</p>
        );
      }
    },
  });

  // Mutation for generating payment metadata
  const [generateLuxeMetadataFn] = useMutation<
    generateLuxePaymentMetadata,
    GenerateLuxePaymentMetadataVariables
  >(GenerateLuxePaymentMetadata, {
    onError(error) {
      if (error?.message !== "unauthenticated") {
        toast.error(
          <p className="toast">{error?.message ?? "An error occured"}</p>
        );
      } else {
        navigate("/clear");
      }
    },
  });

  // Paystack configuration (initially empty, updated in handleSubmit)
  const [paystackConfig, setPaystackConfig] = useState<PaystackProps | null>(
    null
  );

  // continue with pastack payment option
  const handleContinue = async () => {
    if (active === "paystack") {
      try {
        // Call mutation to generate payment metadata
        const { data: mutationData } = await generateLuxeMetadataFn({
          variables: {
            event_id: event.uuid,
            currency: "ngn",
          },
        });

        const paymentMeta = mutationData?.generateLuxePaymentMetadata;

        setPaystackConfig({
          reference: paymentMeta?.payment_reference || "",
          email: userDetails?.user?.email ?? "",
          amount: (paymentMeta?.amount_after_discount ?? 0) * 100 || 0,
          publicKey: process.env.REACT_APP_PAYSTACK_PUBLIC_KEY || "",
          metadata: paymentMeta,
        });
      } catch (error) {
        toast.error("Failed to initiate payment");
        console.error(error);
      }
      setOpen(false);
    } else {
      upgradeToLuxe();
    }
  };

  const onSuccessWithPaystack = () => {
    verifyTransaction({
      variables: {
        reference: paystackConfig?.reference || "",
      },
    });
  };

  // Always call the hook unconditionally with a default config
  const initializePayment = usePaystackPayment(
    paystackConfig || { reference: "", email: "", amount: 0, publicKey: "" }
  );

  useEffect(() => {
    if (paystackConfig) {
      setOpen(false);
      initializePayment(onSuccessWithPaystack);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paystackConfig]);

  // fetch event wallet details
  const { data: eventWalletDetails, loading: eventWalletLoading } = useQuery<
    GetWallets,
    GetWalletsVariables
  >(GetEventWallets, {
    skip: !event.uuid || !isAdmin,
    variables: {
      event_id: event.uuid,
    },
    fetchPolicy: "network-only",
    onError(error) {
      if (error?.message !== "unauthenticated") {
        toast.error(
          <p className="toast">{error?.message ?? "An error occured"}</p>
        );
      } else {
        navigate("/clear");
      }
    },
  });

  return (
    <div className="flex h-auto max-w-[500px] flex-col items-center gap-[30px] rounded-[5px] bg-white">
      <div className="flex w-full flex-col justify-center pb-1 pt-10 lg:px-10">
        <div className="mx-auto mb-7 flex items-center gap-[20px]">
          <InawoGreenLuxeIcon />
          {/* <h3 className="text-[20px] font-medium text-[#298576]">
            Access control
          </h3> */}
        </div>
        <h1 className="mb-5 text-center text-xl font-medium">
          How would you want to pay?
        </h1>
        {/* <CostDisplay cost={cost} /> */}
        <div className="mb-4 flex w-full flex-col gap-4 pt-3">
          <PaymentOption
            type="Paystack"
            active={active === "paystack"}
            onClick={() => updateActiveElement("paystack")}
          />
        </div>
      </div>
      <form
        action=""
        onSubmit={(e) => {
          e.preventDefault();
          handleContinue();
        }}
        className="w-full"
      >
        <SendInvitesModalFooter
          firstButtonText={"Cancel"}
          firstButtonFunction={() => setOpen(false)}
          secondButtonText={"Continue"}
          secondButtonFunction={handleContinue}
          disableSecondButton={active === "" || eventWalletLoading}
          secondButtonLoader={active === "inawo Wallet" && loading}
        />
      </form>
    </div>
  );
};

export default PaymentOptions;
